// VERA Code beside Studio: the VERA Code engine working in a project folder, in a panel on the right.
// For the VERANeural team (desktop only). A code change never lands by itself: it shows as a diff, and only
// "Approve and open PR" writes it, on a branch of its own with a pull request for review.
import { useCallback, useEffect, useRef, useState } from "react";
import { desktop, type CodeEvent, type CodeProject, type CodeStatus } from "../lib/desktop";
import { Markdown } from "./Markdown";
import { Icon } from "./ui";

type DiffFile = { path: string; status: string; added: number; removed: number; patch: string };
type Item =
  | { kind: "user"; text: string }
  | { kind: "line"; level: string; text: string }
  | { kind: "text"; text: string }
  | { kind: "proposal"; summary: string; steps: string[] }
  | { kind: "diff"; files: DiffFile[] }
  | { kind: "ask"; id: number; ask: "choose" | "ask" | "confirm"; question: string; answered?: string };

/** What the engine says for the terminal (its slash commands, the model fallback order), which means nothing in a panel. */
const TERMINAL_ONLY = /^(Talk normally\.|If \S+ is busy, VERA continues)/;
const ANSWERED: Record<string, string> = { apply: "Approved: branch, commit and pull request", revise: "Asked for changes", reject: "Rejected", y: "Yes", n: "No" };

function useCode() {
  const [items, setItems] = useState<Item[]>([]);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<"off" | "starting" | "ready" | "stopped">("off");
  const push = (item: Item) => setItems((xs) => [...xs, item]);

  useEffect(() => desktop?.code?.onEvent((m: CodeEvent) => {
    switch (m.t) {
      case "engine":
        if (m.state === "starting") { setState("starting"); setBusy(false); setItems([]); }
        else if (m.state === "stopped") { setState("stopped"); setBusy(false); if (m.code) push({ kind: "line", level: "warn", text: `VERA Code stopped${m.detail ? `: ${String(m.detail)}` : "."}` }); }
        break;
      case "busy": setBusy(true); setState("ready"); break;
      case "waiting": setBusy(false); setState("ready"); break;
      case "text": {
        const delta = String(m.delta ?? "");
        setItems((xs) => { const last = xs[xs.length - 1]; return last?.kind === "text" ? [...xs.slice(0, -1), { kind: "text", text: last.text + delta }] : [...xs, { kind: "text", text: delta }]; });
        break;
      }
      case "line": { const text = String(m.text ?? "").trim(); if (text && !TERMINAL_ONLY.test(text)) push({ kind: "line", level: String(m.kind ?? "info"), text }); break; }
      case "proposal": push({ kind: "proposal", summary: String(m.summary ?? ""), steps: (m.steps as string[]) ?? [] }); break;
      case "diff": push({ kind: "diff", files: (m.files as DiffFile[]) ?? [] }); break;
      case "ask": push({ kind: "ask", id: Number(m.id), ask: m.kind as "choose" | "ask" | "confirm", question: String(m.question ?? "") }); break;
    }
  }), []);

  const send = useCallback((text: string) => { push({ kind: "user", text }); setBusy(true); void desktop?.code?.send({ t: "message", text }); }, []);
  const answer = useCallback((id: number, value: string) => {
    setItems((xs) => xs.map((x) => (x.kind === "ask" && x.id === id ? { ...x, answered: value } : x)));
    void desktop?.code?.send({ t: "answer", id, value });
  }, []);
  const stop = useCallback(() => { void desktop?.code?.send({ t: "stop" }); }, []);
  const pending = items.some((x) => x.kind === "ask" && x.answered === undefined);
  return { items, busy, state, pending, send, answer, stop };
}

function Diff({ files }: { files: DiffFile[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>(() => Object.fromEntries(files.map((f, i) => [f.path, i < 2])));
  return (
    <div className="cp-diff">
      <div className="cp-diff-head"><b>Proposed changes</b><span className="faint">{files.length} file{files.length === 1 ? "" : "s"} · <span className="cp-add">+{files.reduce((n, f) => n + f.added, 0)}</span> <span className="cp-del">−{files.reduce((n, f) => n + f.removed, 0)}</span></span></div>
      {files.map((f) => (
        <div key={f.path}>
          <button className="cp-file" onClick={() => setOpen((o) => ({ ...o, [f.path]: !o[f.path] }))}>
            <span>{open[f.path] ? "▾" : "▸"}</span><span className="cp-path">{f.path}</span>{f.status !== "changed" && <span className="faint">{f.status}</span>}
            <span className="cp-add">+{f.added}</span><span className="cp-del">−{f.removed}</span>
          </button>
          {open[f.path] && <pre className="cp-patch">{f.patch.split("\n").map((l, i) => <div key={i} className={l.startsWith("@@") ? "hunk" : l.startsWith("+") ? "cp-add" : l.startsWith("-") ? "cp-del" : ""}>{l || " "}</div>)}</pre>}
        </div>
      ))}
    </div>
  );
}

function Ask({ item, answer }: { item: Extract<Item, { kind: "ask" }>; answer: (id: number, v: string) => void }) {
  const [text, setText] = useState("");
  if (item.answered !== undefined) return <div className="cp-line faint">{item.ask === "choose" ? "" : `${item.question} `}→ {ANSWERED[item.answered] ?? (item.answered || "(no answer)")}</div>;
  if (item.ask === "choose") {
    return (
      <div className="cp-approve">
        <span className="faint">Nothing changes until you choose.</span>
        <div className="row" style={{ gap: 6 }}>
          <button className="btn primary" onClick={() => answer(item.id, "apply")}>Approve and open PR</button>
          <button className="btn" onClick={() => answer(item.id, "revise")}>Ask for changes</button>
          <button className="btn" onClick={() => answer(item.id, "reject")}>Reject</button>
        </div>
      </div>
    );
  }
  if (item.ask === "confirm") {
    return (
      <div className="cp-approve"><span>{item.question}</span>
        <div className="row" style={{ gap: 6 }}><button className="btn primary" onClick={() => answer(item.id, "y")}>Yes</button><button className="btn" onClick={() => answer(item.id, "n")}>No</button></div>
      </div>
    );
  }
  return (
    <form className="cp-approve" onSubmit={(e) => { e.preventDefault(); answer(item.id, text); }}>
      <span>{item.question}</span>
      <div className="row" style={{ gap: 6, flexWrap: "nowrap" }}><input className="input" autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Your answer" /><button className="btn primary" type="submit">Send</button></div>
    </form>
  );
}

export function CodePanel({ onClose }: { onClose: () => void }) {
  const code = useCode();
  const [status, setStatus] = useState<CodeStatus | null>(null);
  const [project, setProject] = useState<(CodeProject & { branch: string | null }) | null>(null);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const end = useRef<HTMLDivElement>(null);

  const open = useCallback(async (root: string) => {
    setError("");
    try { setProject(await desktop!.code!.open(root)); } catch (e) { setError(e instanceof Error ? e.message : "VERA Code couldn't open that project."); }
  }, []);

  useEffect(() => {
    void desktop?.code?.status().then((s) => {
      setStatus(s);
      // A fresh page starts a fresh conversation (the old one's questions can't be answered from here).
      const root = s.current?.root ?? s.last;
      if (s.engine && root) void open(root);
    }).catch((e: Error) => setError(e.message));
  }, [open]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [code.items, code.busy]);
  // VERA Code may have moved to a new branch (an approved change goes on its own branch): show where the project is now.
  useEffect(() => {
    if (code.busy || code.state !== "ready") return;
    void desktop?.code?.status().then((s) => { if (s.current) setProject(s.current); }).catch(() => { /* keep what's shown */ });
  }, [code.busy, code.state]);

  async function addProject() {
    const r = await desktop!.code!.addProject().catch((e: Error) => { setError(e.message); return null; });
    if (!r) return;
    setStatus((s) => (s ? { ...s, projects: r.projects, last: r.last } : s));
    if (r.last) void open(r.last);
  }
  const submit = () => { const t = text.trim(); if (!t || code.busy || !project) return; setText(""); code.send(t); };

  return (
    <aside className="codepanel" aria-label="VERA Code">
      <div className="cp-head">
        <Icon name="code" size={17} /><b>VERA Code</b>
        <span className="cp-team">Team</span>
        <button className="iconbtn ghost" style={{ marginLeft: "auto" }} onClick={onClose} aria-label="Close VERA Code" title="Close"><Icon name="x" size={16} /></button>
      </div>

      {status && status.engine && (
        <div className="cp-proj">
          <select className="input" value={project?.root ?? ""} onChange={(e) => { if (e.target.value === "+") void addProject(); else if (e.target.value) void open(e.target.value); }} aria-label="Project">
            {!project && <option value="">Choose a project…</option>}
            {status.projects.map((p) => <option key={p.root} value={p.root}>{p.name}</option>)}
            <option value="+">Open another folder…</option>
          </select>
          {project?.branch && <span className={`cp-branch ${/^(main|master)$/.test(project.branch) ? "warn" : ""}`} title={/^(main|master)$/.test(project.branch) ? "Approved changes go on a new branch with a pull request, never straight to main." : "Current branch"}>{project.branch}</span>}
        </div>
      )}

      <div className="cp-conv">
        {!status && !error && <div className="cp-line faint">Opening VERA Code…</div>}
        {status && !status.engine && <div className="notice calm"><span>VERA Code isn't installed on this computer yet. Install it, sign in once, then open this panel again.</span></div>}
        {status?.engine && status.projects.length === 0 && (
          <div className="cp-empty">
            <p className="mute">Open a project folder, for example the VERA Studio repository, and ask VERA Code to change it. Every change waits for your Approve and goes out as a pull request.</p>
            <button className="btn primary" onClick={() => void addProject()}><Icon name="folder" size={15} />Open a project folder</button>
          </div>
        )}
        {project && code.items.length === 0 && code.state !== "starting" && <div className="cp-line faint">Ready in {project.name}. Ask for a change, a fix or an explanation.</div>}
        {code.state === "starting" && <div className="cp-line faint">Starting VERA Code in {project?.name ?? "the project"}…</div>}
        {code.items.map((it, i) => {
          switch (it.kind) {
            case "user": return <div key={i} className="cp-user">{it.text}</div>;
            case "text": return <div key={i} className="cp-vera"><Markdown text={it.text} /></div>;
            case "line": return <div key={i} className={`cp-line ${it.level === "warn" ? "warn" : it.level === "success" ? "ok" : "faint"}`}>{it.text}</div>;
            case "proposal": return <div key={i} className="cp-card"><b>Plan</b><p>{it.summary}</p>{it.steps.length > 0 && <ol>{it.steps.map((s, j) => <li key={j}>{s}</li>)}</ol>}</div>;
            case "diff": return <Diff key={i} files={it.files} />;
            case "ask": return <Ask key={i} item={it} answer={code.answer} />;
          }
        })}
        {code.busy && !code.pending && <div className="cp-line faint">VERA Code is working…</div>}
        {error && <div className="cp-line warn" role="alert">{error}</div>}
        <div ref={end} />
      </div>

      <div className="cp-compose">
        <textarea value={text} onChange={(e) => setText(e.target.value)} disabled={!project} placeholder={project ? `Ask VERA Code to change ${project.name}…` : "Choose a project first"}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }} />
        {code.busy
          ? <button className="btn" onClick={code.stop}><Icon name="stop" size={15} />Stop</button>
          : <button className="btn primary" disabled={!project || !text.trim()} onClick={submit}><Icon name="send" size={15} />Send</button>}
      </div>
    </aside>
  );
}
